import * as React from "react";
import { FunctionComponent } from "react";
import getIcon from "./icons";
import { IconName as TIconName } from "./type";
import {
  styled,
  unit,
  IColor,
  IColors,
  renderColor,
} from "../../styled-components";
import { Block, IBlockProps } from "../Block/Block";

type IconName = TIconName;

const isBase64 = (str: string) => {
  if (str === "" || str.trim() === "") {
    return false;
  }
  try {
    return btoa(atob(str)) == str;
  } catch (err) {
    return false;
  }
};

type iconSizeType =
  | "free"
  | "smallest"
  | "smaller"
  | "small"
  | "small2"
  | "base"
  | "medium"
  | "large"
  | "larger"
  | "largest";

interface IIconProps extends IBlockProps {
  /**
   * Name of the Icon
   * from iconName type.
   * @example 'calendar'
   */
  name?: IconName | null;
  /**
   *
   * @summary to use external svg in your app use:
   * @example import { ReactComponent as SearchIcon } from './search.svg'
   */
  source?: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  overlayMedia?: any;
  overlayMediaProps?: any;
  strokeColor?: keyof IColors;
  strokeShade?: keyof IColor;
  iconSize?: iconSizeType;
  iconFill?: string;
  /**
   * Rewrite the rendered SVG's internal ids (gradients, clipPaths, filters)
   * and their `url(#id)` references to be unique per instance. Use this when
   * the same icon can be rendered more than once on a page (e.g. in a list and
   * in a menu) and its colored SVG defines fixed ids. Without it, duplicate ids
   * collide and `url(#id)` resolves to the wrong (possibly hidden) def, which
   * can make the icon render blank. See SEE-512.
   */
  uniqueIds?: boolean;
  onClick?:
    | ((event: React.MouseEvent<HTMLDivElement, MouseEvent>) => void)
    | undefined;
}

export const IconContainer = styled(Block).attrs({})<IIconProps>`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${({ theme, iconSize }) =>
    iconSize !== "free" && unit(theme.icon.size[iconSize || "base"])};
  height: ${({ theme, iconSize }) =>
    iconSize !== "free" && unit(theme.icon.size[iconSize || "base"])};
  svg {
    fill: ${({ iconFill }) => iconFill};
    path {
      fill: ${({ iconFill }) => iconFill};
    }

    circle {
      stroke: ${({ theme, strokeColor, strokeShade }) =>
        renderColor(theme, strokeColor, strokeShade)};
    }
  }
  * {
    width: 100%;
    height: 100%;
  }
`;

const OverlayMedia = styled(Block).attrs({})<{
  mediaUrl: string;
  isMediaBase64: boolean;
}>`
  right: -5px;
  bottom: -5px;
  z-index: 100;
  position: absolute;
  height: 18px;
  width: 18px;
  border-radius: 40%;
  background-color: green;
  background-image: ${({ mediaUrl, isMediaBase64 }) => {
    return isMediaBase64
      ? `url(data:image/jpeg;base64, ${mediaUrl})`
      : `url(${mediaUrl})`;
  }};
  background-size: contain;
  box-shadow: 0px 0px 5px #00000063;
  background-repeat: no-repeat;
`;

/**
 * Rewrite all `url(#id)` / `href="#id"` references to the given ids inside a
 * string (an attribute value or a <style> block), matching every form the
 * reference can take. Crucially this includes the QUOTED forms (url("#id") /
 * url('#id')): SVGR turns an SVG `style="...fill:url(#x)"` attribute into a
 * React style object, which the browser applies via the CSSOM and serializes
 * back WITH quotes (url("#x")). Matching only the bare url(#id) form left those
 * references dangling, which was why gradient icons (e.g. Viva Engage) still
 * went blank. See SEE-512.
 */
const rewriteRefs = (
  value: string,
  renameMap: { [oldId: string]: string }
): string => {
  if (value.indexOf("#") === -1) return value;
  let next = value;
  Object.keys(renameMap).forEach((oldId) => {
    const newId = renameMap[oldId];
    ["", '"', "'"].forEach((q) => {
      next = next
        .split(`url(${q}#${oldId}${q})`)
        .join(`url(${q}#${newId}${q})`);
    });
    // (xlink:)href="#id" — the whole attribute value is just the fragment
    if (next === `#${oldId}`) next = `#${newId}`;
  });
  return next;
};

/**
 * Rewrite every `id` defined inside an inlined SVG (and the references to them)
 * so they carry a per-instance suffix. This prevents id collisions when the
 * same icon is rendered more than once on a page. Best-effort: any failure
 * leaves the SVG untouched.
 */
export const makeSvgIdsUnique = (
  root: Element | null | undefined,
  suffix: string
) => {
  if (!root) return;
  try {
    const renameMap: { [oldId: string]: string } = {};
    root.querySelectorAll("[id]").forEach((el) => {
      const oldId = el.getAttribute("id");
      if (oldId) renameMap[oldId] = `${oldId}-${suffix}`;
    });

    if (Object.keys(renameMap).length === 0) return;

    const elements: Element[] = [root].concat(
      Array.prototype.slice.call(root.querySelectorAll("*"))
    );
    elements.forEach((el) => {
      Array.prototype.slice.call(el.attributes).forEach((attr: Attr) => {
        const newValue = rewriteRefs(attr.value, renameMap);
        if (newValue !== attr.value) el.setAttribute(attr.name, newValue);
      });

      const elId = el.getAttribute("id");
      if (elId && renameMap[elId]) el.setAttribute("id", renameMap[elId]);
    });

    // References can also live inside <style> blocks (textContent), not only
    // attributes.
    Array.prototype.slice
      .call(root.querySelectorAll("style"))
      .forEach((styleEl: Element) => {
        const text = styleEl.textContent || "";
        const newText = rewriteRefs(text, renameMap);
        if (newText !== text) styleEl.textContent = newText;
      });
  } catch (e) {
    // best-effort only
  }
};

let uniqueIconSeq = 0;

const UniqueIconSvg: FunctionComponent<{
  Svg: SvgrComponent;
  name: string;
}> = ({ Svg, name }) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const suffixRef = React.useRef<string | undefined>(undefined);
  if (suffixRef.current === undefined) {
    uniqueIconSeq += 1;
    suffixRef.current = `i${uniqueIconSeq}`;
  }
  React.useLayoutEffect(() => {
    const svg = ref.current && ref.current.querySelector("svg");
    makeSvgIdsUnique(svg, suffixRef.current as string);
  }, [name]);

  return (
    <span ref={ref} style={{ display: "contents" }}>
      <Svg name={name} />
    </span>
  );
};

const Icon: FunctionComponent<IIconProps> = ({
  overlayMedia,
  overlayMediaProps,
  iconFill,
  uniqueIds,
  ...props
}) => {
  const Svg = props.name && getIcon(props.name);
  const ExternalSvg = props.source;
  return (
    <IconContainer {...props}>
      {overlayMedia && (
        <OverlayMedia
          isMediaBase64={isBase64(overlayMedia) ? true : false}
          mediaUrl={overlayMedia}
          {...overlayMediaProps}
        />
      )}
      {Svg && uniqueIds && (
        <UniqueIconSvg Svg={Svg} name={props.name || ""} />
      )}
      {Svg && !uniqueIds && <Svg name={props.name || ""} />}
      {!Svg && ExternalSvg && <ExternalSvg />}
    </IconContainer>
  );
};

export { Icon, IIconProps, IconName };
export default Icon;
